import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import api from '../services/api';

const Container = styled.div`
  padding: 2.5rem 2rem;
  max-width: 900px;
  margin: 2.5rem auto;
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.96);
  border-radius: 16px;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.16);
  padding: 2rem;
`;

const Title = styled.h1`
  color: ${({ theme }) => theme.colors.highlight};
  margin-bottom: 1rem;
`;

const Subtitle = styled.p`
  margin: 0 0 1.5rem;
  font-size: 0.95rem;
  color: #4b5563;
`;

const SectionTitle = styled.h2`
  margin: 1.25rem 0 0.5rem;
  font-size: 1.05rem;
  font-weight: 600;
  color: #111827;
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const Item = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  background: #f3f4f6;
`;

const ItemLink = styled(Link)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  text-decoration: none;
  color: inherit;
`;

const ItemMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
`;

const ItemTitle = styled.div`
  font-size: 0.95rem;
  font-weight: 500;
  color: #111827;
`;

const ItemMeta = styled.div`
  font-size: 0.85rem;
  color: #4b5563;
`;

const ItemTime = styled.div`
  font-size: 0.85rem;
  font-weight: 500;
  color: #111827;
`;

const ItemActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const DangerButton = styled.button`
  padding: 0.3rem 0.7rem;
  border-radius: 999px;
  border: none;
  background: #b91c1c;
  color: #f9fafb;
  font-size: 0.75rem;
  cursor: pointer;

  &:hover {
    background: #dc2626;
  }
`;

const EmptyState = styled.p`
  margin: 0;
  font-size: 0.9rem;
  color: #6b7280;
`;

const Appointments: React.FC = () => {
  const [appointments, setAppointments] = useState<any[]>([]);
  const [consultations, setConsultations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    const fetchAppointments = async () => {
      setLoading(true);
      setError('');
      try {
        const [apptRes, bookingsRes] = await Promise.all([
          api.get('/appointments/artist/my'),
          api.get('/bookings'),
        ]);

        const apptData = apptRes.data?.data || [];
        const bookingData = bookingsRes.data || [];

        setAppointments(apptData);
        setConsultations(Array.isArray(bookingData) ? bookingData : []);
      } catch (err: any) {
        console.error('Failed to load appointments', err);
        const message = err?.response?.data?.message || 'Could not load appointments.';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    fetchAppointments();
  }, []);

  const handleDeleteAppointment = async (id: string) => {
    if (!window.confirm('Delete this appointment? This cannot be undone.')) return;

    try {
      setDeletingId(id);
      await api.delete(`/appointments/${id}`);
      setAppointments(prev => prev.filter(appt => appt._id !== id));
    } catch (err) {
      console.error('Failed to delete appointment', err);
      alert('Could not delete appointment.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleAddConsultationNote = async (id: string) => {
    const note = window.prompt('Add a private note for this consultation:');
    if (!note || !note.trim()) return;

    try {
      const res = await api.post(`/bookings/${id}/notes`, { note });
      const updated = res.data;
      setConsultations(prev => prev.map(b => (b._id === id ? updated : b)));
    } catch (err) {
      console.error('Failed to add consultation note', err);
      alert('Could not save note.');
    }
  };

  const handleDeleteConsultation = async (id: string) => {
    if (!window.confirm('Delete this consultation request? This cannot be undone.')) return;

    try {
      setDeletingId(id);
      await api.delete(`/bookings/${id}`);
      setConsultations(prev => prev.filter(b => b._id !== id));
    } catch (err) {
      console.error('Failed to delete consultation request', err);
      alert('Could not delete consultation request.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Container>
      <Card>
        <Title>Your appointments</Title>
        <Subtitle>
          A simple list of all confirmed appointments and consultation requests associated with your artist account.
        </Subtitle>

        {loading ? (
          <EmptyState>Loading appointments…</EmptyState>
        ) : error ? (
          <EmptyState>{error}</EmptyState>
        ) : (
          <>
            <SectionTitle>Upcoming appointments</SectionTitle>
            {appointments.length === 0 ? (
              <EmptyState>You don&apos;t have any appointments yet.</EmptyState>
            ) : (
              <List>
                {appointments.map((appt) => {
                  const start = appt.startTime ? new Date(appt.startTime) : null;
                  const clientName =
                    appt.client?.profile?.firstName ||
                    appt.client?.email ||
                    'Client';

                  return (
                    <Item key={appt._id}>
                      <ItemMain>
                        <ItemTitle>{appt.service || 'Tattoo session'}</ItemTitle>
                        <ItemMeta>
                          {clientName}
                        </ItemMeta>
                      </ItemMain>
                      <ItemActions>
                        <ItemTime>
                          {start
                            ? start.toLocaleString(undefined, {
                                dateStyle: 'medium',
                                timeStyle: 'short',
                              })
                            : 'TBD'}
                        </ItemTime>
                        <DangerButton
                          type="button"
                          onClick={() => handleDeleteAppointment(appt._id)}
                          disabled={deletingId === appt._id}
                        >
                          {deletingId === appt._id ? 'Deleting…' : 'Delete'}
                        </DangerButton>
                      </ItemActions>
                    </Item>
                  );
                })}
              </List>
            )}

            <SectionTitle>Consultation requests</SectionTitle>
            {consultations.length === 0 ? (
              <EmptyState>You don&apos;t have any consultation requests yet.</EmptyState>
            ) : (
              <List>
                {consultations.map((booking) => (
                  <Item key={booking._id}>
                    <ItemLink to={`/bookings/${booking._id}`}>
                      <ItemMain>
                        <ItemTitle>{booking.name || 'Consultation request'}</ItemTitle>
                        <ItemMeta>
                          {booking.email || booking.phone || 'No contact info'}
                          {Array.isArray(booking.internalNotes) && booking.internalNotes.length > 0 && (
                            <>
                              {' '}
                              • {booking.internalNotes.length} note{booking.internalNotes.length > 1 ? 's' : ''}
                              <div>
                                Latest note: {booking.internalNotes[booking.internalNotes.length - 1].content}
                              </div>
                            </>
                          )}
                        </ItemMeta>
                      </ItemMain>
                      <ItemActions>
                        <ItemTime>
                          {booking.createdAt
                            ? new Date(booking.createdAt).toLocaleDateString(undefined, {
                                dateStyle: 'medium',
                              })
                            : ''}
                        </ItemTime>
                        <DangerButton
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleDeleteConsultation(booking._id);
                          }}
                          disabled={deletingId === booking._id}
                        >
                          {deletingId === booking._id ? 'Deleting…' : 'Delete'}
                        </DangerButton>
                        <DangerButton
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleAddConsultationNote(booking._id);
                          }}
                          disabled={false}
                          style={{ background: '#374151' }}
                        >
                          Add note
                        </DangerButton>
                      </ItemActions>
                    </ItemLink>
                  </Item>
                ))}
              </List>
            )}
          </>
        )}
      </Card>
    </Container>
  );
};

export default Appointments;
